import LinkifyIt, { type Match } from "linkify-it";

const PrepositionsSearchedElements = "h1, h2, h3, h4, p";

export function escapeForRegex(str: string): string {
  const REGEXP_SPECIAL_CHAR = /[!#$%^&*)(+=.<>{}[\]:;'"|~`_-]/g;
  return str.replace(REGEXP_SPECIAL_CHAR, "\\$&");
}

export async function wrapPrepositions(container?: HTMLElement, searchFor: string = PrepositionsSearchedElements): Promise<void> {
  if (!container) {
    container = document.body;
  }

  container.querySelectorAll(searchFor).forEach((element) => {
    wrapPrepositionsInTextNodes(element);
  });
}

function wrapPrepositionsInTextNodes(node: ChildNode): void {
  if (node.nodeName === "#text") {
    const value = node.nodeValue;

    if (value) {
      node.nodeValue = value.replace(/\b([a-z0-9])(\s+)/gim, "$1\xa0");
    }
  } else {
    node.childNodes.forEach((childNode) => {
      wrapPrepositionsInTextNodes(childNode);
    });
  }
}

export function truncate(str: string, maxLength: number): string {
  if (str.length > maxLength) {
    return str.slice(0, maxLength) + "…";
  } else {
    return str;
  }
}

export function createTelHref(tel: string): string {
  return "tel:" + tel.replaceAll("+", "00").replace(/\D+/g, "");
}

export const escapeHtml = (text: string): string => {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
};

type UrlsToLinkConfig = Partial<{
  addTargetBlank: boolean;
  addNoOpener: boolean;
  // Feel free to add more options if needed
}>;

export const convertUrlsToLinks = (text: string, config: UrlsToLinkConfig = {}): string => {
  const { addTargetBlank, addNoOpener } = config;
  const linkifier = new LinkifyIt();

  const matches = linkifier.match(text);
  if (matches === null) {
    return text;
  }

  const attributesArray = [];
  if (addTargetBlank) {
    attributesArray.push('target="_blank"');
  }
  if (addNoOpener) {
    attributesArray.push('rel="noopener"');
  }
  const attributesString = attributesArray.join(" ");

  let result = text;
  matches.forEach((match: Match) => {
    const url = match.raw;
    const tag = `<a href="${url}" ${attributesString}>${url}</a>`;
    result = result.replace(url, tag);
  });
  return result;
};

/**
 * Splits the input string into two parts while keeping words intact.
 * The function aims to balance the total character count between both parts.
 * If an exact balance is not possible, the second part will be slightly longer.
 */
export const splitStringBalanced = (input: string): [string, string] => {
  const str = input.replace(/\s+/g, " ").trim(); // normalize whitespace
  const words = str.split(" ");
  const wordsCount = words.length;

  const part1: string[] = [];
  const part2: string[] = [];

  if (wordsCount === 1) {
    return [words[0], ""];
  } else if (wordsCount === 2) {
    return [words[0], words[1]];
  }

  for (const word of words) {
    const line1candidate = [...part1, word];
    const line1WithWordLength = line1candidate.join(" ").length;
    const remainingWords = words.slice(words.indexOf(word) + 1);
    const remainingWordsLength = remainingWords.join(" ").length;
    if (line1WithWordLength > remainingWordsLength) {
      part2.push(word);
    } else {
      part1.push(word);
    }
  }

  return [part1.join(" "), part2.join(" ")];
};

export function assertString(value: unknown): asserts value is string {
  if (typeof value !== "string") {
    throw new Error(`Expected string, got ${typeof value}`);
  }
}
